/**
 * AreaXRayPage — "The Area X-Ray" (AREA_SCORING_DESIGN §3).
 *
 * An X-ray of an area's judgement: the composite demoted to a summary line
 * beside its coverage, the dimension rows showing what each contributed,
 * the what-if weighting control, the WHY panel one interaction from any
 * number, and an honest places digest. Compare mode answers "versus where
 * else" from the same endpoints the rest of the page uses.
 *
 * Data contract (types file documents why both are needed):
 *   - GET /api/areas/:slug — the cards (dimension metadata incl. unscored).
 *   - GET /api/areas/:slug/scores — the calculation rows; the composite's
 *     input_formula.weightedComponents is the only exact decomposition.
 *
 * All /api/areas* endpoints are authenticated (PR 580, same window) — the
 * app client sends the bearer like every other page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useLocale } from '@/hooks/useLocale'
import { usePageTitle } from '@/lib/usePageTitle'
import type {
  AreaListItem,
  AreaProfile,
  AreaScoreCalculation,
  AreaScoreCard,
} from '@/types/areaIntelligence'
import {
  buildCompositeView,
  defaultWeights,
  recomputeComposite,
  reweight,
} from './areaScoring'
import { areaXRayT, type AreaXRayLocale } from './copy'
import { ContributionChart } from './ContributionChart'
import { WhatIfPanel } from './WhatIfPanel'
import { WhyPanel, type WhySelection } from './WhyPanel'
import { AroundAreaChips } from './AroundAreaChips'
import { digestPlaces } from './placesDigest'

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'not-found' }
  | {
      phase: 'ready'
      area: AreaProfile
      cards: AreaScoreCard[]
      calculations: AreaScoreCalculation[]
      placesRows: unknown[]
      properties: Array<Record<string, unknown>>
    }

interface ComparisonState {
  area: AreaListItem
  payload: {
    comparisons: Array<{
      dimension: AreaScoreCard['dimension']
      area_a: { score: number | null; confidence: number | null }
      area_b: { score: number | null; confidence: number | null }
    }>
  }
}

/** Arabic label from the data when present, English otherwise — a blank is a bug (§11). */
function dimensionLabel(dimension: AreaScoreCard['dimension'], locale: AreaXRayLocale): string {
  if (locale === 'ar' && dimension.name_ar) return dimension.name_ar
  return dimension.name
}

export function AreaXRayPage() {
  const { slug = '' } = useParams()
  const { locale, dir } = useLocale()
  const t = (key: Parameters<typeof areaXRayT>[0], vars?: Record<string, string | number>) =>
    areaXRayT(key, locale, vars)
  usePageTitle(t('pageTitle'))

  const [state, setState] = useState<LoadState>({ phase: 'loading' })
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [weights, setWeights] = useState<Record<string, number> | null>(null)
  const [compareOptions, setCompareOptions] = useState<AreaListItem[] | null>(null)
  const [comparison, setComparison] = useState<ComparisonState | null>(null)
  // Request sequencing: a stale response (older slug / superseded fetch)
  // must never overwrite a newer one.
  const requestSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    setState({ phase: 'loading' })
    try {
      const [areaPayload, scoresPayload, placesPayload, propertiesPayload] = await Promise.all([
        api.getArea(slug),
        api.getAreaScores(slug),
        api.getAreaGoogleScores(slug),
        api.getAreaProperties(slug, { limit: '50' }),
      ])
      if (seq !== requestSeq.current) return
      if (!areaPayload?.area) {
        // Empty or unknown slug (including a missing :slug route param).
        setState({ phase: 'not-found' })
        return
      }
      setState({
        phase: 'ready',
        area: areaPayload.area as AreaProfile,
        cards: (areaPayload.scores || []) as AreaScoreCard[],
        calculations: (scoresPayload.scores || []) as AreaScoreCalculation[],
        placesRows: (placesPayload.items || []) as unknown[],
        properties: (propertiesPayload.items || []) as Array<Record<string, unknown>>,
      })
    } catch (err) {
      if (seq !== requestSeq.current) return
      setState({ phase: err && (err as { status?: number }).status === 404 ? 'not-found' : 'error' })
    }
  }, [slug])

  useEffect(() => {
    void load()
    setSelectedSlug(null)
    setWeights(null)
    setComparison(null)
  }, [load])

  const view = useMemo(
    () => (state.phase === 'ready' ? buildCompositeView(state.cards, state.calculations) : null),
    [state],
  )

  // Weights start unset = platform weights; they materialise once the view does.
  const effectiveWeights = weights ?? (view ? defaultWeights(view.contributions) : {})
  const adjustedComposite = view ? recomputeComposite(view.contributions, effectiveWeights) : null
  const adjusted =
    view != null &&
    weights != null &&
    view.contributions.some((entry) => Number(effectiveWeights[entry.slug]) !== entry.weight)

  const scoredCount = state.phase === 'ready' ? state.cards.filter((c) => c.score != null).length : 0
  const totalCount = state.phase === 'ready' ? state.cards.length : 0
  const displayComposite = adjustedComposite ?? view?.platformScore ?? null

  const unscoredRows = useMemo(() => {
    if (state.phase !== 'ready' || !view) return []
    const contributing = new Set(view.contributions.map((entry) => entry.slug))
    return state.cards
      .filter(
        (card) =>
          card.dimension.slug !== 'overall_livability' &&
          card.score == null &&
          !contributing.has(card.dimension.slug),
      )
      .map((card) => ({ slug: card.dimension.slug, label: dimensionLabel(card.dimension, locale) }))
  }, [state, view, locale])

  // Labels are a display concern: the vendored maths is locale-agnostic and
  // builds English labels from dimension.name. Relabel per locale from the
  // cards (name_ar with an English fallback — a blank label is a bug, §11).
  const cardBySlug = useMemo(() => {
    if (state.phase !== 'ready') return new Map<string, AreaScoreCard>()
    return new Map(state.cards.map((card) => [card.dimension.slug, card]))
  }, [state])

  const localize = useCallback(
    (entry: { slug: string; label: string }): string => {
      const card = cardBySlug.get(entry.slug)
      return card ? dimensionLabel(card.dimension, locale) : entry.label
    },
    [cardBySlug, locale],
  )

  const whySelection: WhySelection | null = useMemo(() => {
    if (!view || !selectedSlug) return null
    const contribution = view.contributions.find((entry) => entry.slug === selectedSlug)
    if (!contribution) return null
    const calc =
      state.phase === 'ready'
        ? state.calculations.find((row) =>
            state.cards.some(
              (card) => card.dimension.slug === selectedSlug && card.dimension.id === row.dimension_id,
            ),
          )
        : undefined
    const rawComponents = calc?.input_formula?.components
    const signals = Array.isArray(rawComponents)
      ? (rawComponents as Array<{ source_type?: string; reliability?: number; observed_at?: string }>)
      : []
    return {
      contribution: { ...contribution, label: localize(contribution) },
      signals,
      calculatedAt: calc?.calculated_at ?? null,
    }
  }, [view, selectedSlug, state, localize])

  const digest = useMemo(
    () => (state.phase === 'ready' ? digestPlaces(state.placesRows as never) : []),
    [state],
  )

  const openComparePicker = async () => {
    if (compareOptions) return
    const result = await api.listAreas()
    setCompareOptions(((result.items || []) as AreaListItem[]).filter((item) => item.slug !== slug))
  }

  const pickComparison = async (otherSlug: string) => {
    if (!otherSlug) {
      setComparison(null)
      return
    }
    const payload = await api.compareAreas(slug, otherSlug)
    const area = compareOptions?.find((item) => item.slug === otherSlug) ?? {
      id: otherSlug,
      name: otherSlug,
      slug: otherSlug,
      level: '',
    }
    setComparison({ area, payload: payload as ComparisonState['payload'] })
  }

  if (state.phase === 'loading') {
    // Skeleton matching the final layout — no layout shift (§8).
    return (
      <div data-testid="xray-loading" aria-busy="true" style={{ padding: 24, fontFamily: 'var(--lc-font-ui)' }}>
        <p style={{ color: 'var(--lc-text-muted)' }}>{t('loading')}</p>
        <div style={{ height: 24, width: '40%', borderRadius: 6, background: 'var(--lc-surface-sunken)', marginBottom: 12 }} />
        <div style={{ height: 320, borderRadius: 12, background: 'var(--lc-surface-sunken)' }} />
      </div>
    )
  }

  if (state.phase === 'not-found') {
    return (
      <div data-testid="xray-not-found" style={{ padding: 24, fontFamily: 'var(--lc-font-ui)' }}>
        <p style={{ color: 'var(--lc-text-secondary)' }}>{t('areaNotFound')}</p>
        <button type="button" onClick={() => void load()} style={{ color: 'var(--lc-action-primary)' }}>
          {t('retry')}
        </button>
      </div>
    )
  }

  if (state.phase === 'error' || !view) {
    return (
      <div data-testid="xray-error" style={{ padding: 24, fontFamily: 'var(--lc-font-ui)' }}>
        <p style={{ color: 'var(--lc-text-secondary)' }}>{t('failedToLoad')}</p>
        <button type="button" onClick={() => void load()} style={{ color: 'var(--lc-action-primary)' }}>
          {t('retry')}
        </button>
      </div>
    )
  }

  const { area, properties } = state
  const areaName = locale === 'ar' && area.name_ar ? area.name_ar : area.name
  const anyScores = view.platformScore != null || view.contributions.length > 0
  const visibleContributions = (
    adjusted ? reweight(view.contributions, effectiveWeights) : view.contributions
  ).map((entry) => ({ ...entry, label: localize(entry) }))
  const whatIfContributions = view.contributions.map((entry) => ({ ...entry, label: localize(entry) }))

  return (
    <div data-testid="area-xray" data-dir={dir} style={{ padding: 24, fontFamily: 'var(--lc-font-ui)', maxWidth: 1080 }}>
      {/* Headline — composite + coverage are never separated (§6). */}
      <header style={{ marginBottom: 20 }}>
        <p style={{ margin: 0, color: 'var(--lc-text-muted)' }}>
          {area.level} · {areaName}
        </p>
        {anyScores ? (
          <h1 style={{ margin: '4px 0', color: 'var(--lc-text-heading)', fontSize: 24 }}>
            {t('compositeLabel')}{' '}
            <span data-testid="headline-score" style={{ fontFamily: 'var(--lc-font-mono)' }}>
              {displayComposite != null ? displayComposite.toFixed(1) : '—'}
            </span>{' '}
            / 10
            <span data-testid="coverage-line" style={{ fontSize: 14, color: 'var(--lc-text-muted)', fontWeight: 400 }}>
              {' '}
              · {t('coverageScored', { scored: scoredCount, total: totalCount })}
            </span>
          </h1>
        ) : (
          <h1 style={{ margin: '4px 0', color: 'var(--lc-text-heading)', fontSize: 24 }}>
            <span data-testid="headline-score" style={{ fontFamily: 'var(--lc-font-mono)' }}>
              —
            </span>{' '}
            {t('notScoredYet')}
            <span data-testid="coverage-line" style={{ fontSize: 14, color: 'var(--lc-text-muted)', fontWeight: 400 }}>
              {' '}
              · {t('coverageScored', { scored: 0, total: totalCount })}
            </span>
          </h1>
        )}
        <p style={{ margin: 0, color: 'var(--lc-text-secondary)' }}>{area.summary ?? t('generatedSummaryFallback')}</p>
      </header>

      {/* The X-ray + what-if, side by side on desktop; stacked on phone. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <section aria-label={t('xrayEyebrow')}>
          <p style={{ margin: 0, fontWeight: 700, color: 'var(--lc-text-heading)' }}>{t('xrayEyebrow')}</p>
          <p style={{ margin: '2px 0 10px', color: 'var(--lc-text-muted)', fontSize: 13 }}>{t('xrayHint')}</p>
          <ContributionChart
            contributions={visibleContributions}
            unscored={unscoredRows}
            compositeScore={displayComposite}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
            dir={dir}
            locale={locale}
          />
        </section>

        <WhatIfPanel
          contributions={whatIfContributions}
          weights={effectiveWeights}
          onWeightChange={(dimensionSlug, value) =>
            setWeights((prev) => ({ ...(prev ?? defaultWeights(view.contributions)), [dimensionSlug]: value }))
          }
          onReset={() => setWeights(null)}
          adjustedComposite={adjustedComposite}
          platformComposite={view.platformScore}
          adjusted={adjusted}
          dir={dir}
          locale={locale}
        />
      </div>

      {/* WHY + Around the area. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
          marginTop: 24,
        }}
      >
        <WhyPanel selection={whySelection} locale={locale} />
        <AroundAreaChips digest={digest} locale={locale} />
      </div>

      {/* Compare mode (§8). */}
      <section data-testid="compare-section" style={{ marginTop: 24 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--lc-text-primary)' }}>
          {t('compareWith')}
          <select
            data-testid="compare-select"
            defaultValue=""
            onFocus={() => void openComparePicker()}
            onChange={(event) => void pickComparison(event.target.value)}
            style={{ fontFamily: 'var(--lc-font-ui)', padding: '4px 8px', borderRadius: 8, border: '1px solid var(--lc-border-strong)', background: 'var(--lc-surface-raised)', color: 'var(--lc-text-primary)' }}
          >
            <option value="">{t('pickAreaPlaceholder')}</option>
            {(compareOptions || []).map((item) => (
              <option key={item.id} value={item.slug}>
                {locale === 'ar' && item.name_ar ? item.name_ar : item.name}
              </option>
            ))}
          </select>
        </label>

        {comparison && (
          <div data-testid="comparison-result" style={{ marginTop: 12, overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 420, color: 'var(--lc-text-primary)' }}>
              <thead>
                <tr style={{ color: 'var(--lc-text-muted)', textAlign: 'start' }}>
                  <th style={{ textAlign: 'start', padding: '6px 10px' }}>{t('compositeRow')}</th>
                  <th style={{ textAlign: 'start', padding: '6px 10px' }}>{areaName}</th>
                  <th style={{ textAlign: 'start', padding: '6px 10px' }}>
                    {locale === 'ar' && comparison.area.name_ar ? comparison.area.name_ar : comparison.area.name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.payload.comparisons.map((row) => (
                  <tr key={row.dimension.slug} style={{ borderTop: '1px solid var(--lc-border)' }}>
                    <td style={{ padding: '6px 10px' }}>{dimensionLabel(row.dimension, locale)}</td>
                    <td data-testid={`cmp-a-${row.dimension.slug}`} style={{ padding: '6px 10px', fontFamily: 'var(--lc-font-mono)' }}>
                      {row.area_a.score != null ? row.area_a.score.toFixed(1) : t('unscored')}
                    </td>
                    <td data-testid={`cmp-b-${row.dimension.slug}`} style={{ padding: '6px 10px', fontFamily: 'var(--lc-font-mono)' }}>
                      {row.area_b.score != null ? row.area_b.score.toFixed(1) : t('unscored')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Collapsible tails (§3). */}
      <details style={{ marginTop: 20, color: 'var(--lc-text-primary)' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('propertiesSection')}</summary>
        {properties.length ? (
          <ul style={{ paddingInlineStart: 20 }}>
            {properties.map((property) => (
              <li key={String(property.id)}>{String(property.title ?? property.id)}</li>
            ))}
          </ul>
        ) : (
          <p style={{ color: 'var(--lc-text-muted)' }}>{t('noProperties')}</p>
        )}
      </details>

      <details style={{ marginTop: 12, color: 'var(--lc-text-primary)' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('outlookSection')}</summary>
        <p style={{ color: 'var(--lc-text-secondary)' }}>{area.investment_outlook ?? t('noOutlook')}</p>
      </details>
    </div>
  )
}
